import { useEffect, useState } from 'react';
import { useDataProvider } from '@ui-framework/index';
import { ISSUE_ENTITY_NAME, ISSUE_PERMISSIONS_API_ROOT } from './constants';
import { getProviderName } from './utils';

// Fail closed: the backend is authoritative, so an unreachable or failed
// permissions call must not unlock any action or field.
const EMPTY_PERMISSIONS = { actions: [], fields: {} };

export const buildIssuePermissionsParams = entityStatus => ({
  entityName: ISSUE_ENTITY_NAME,
  ...(entityStatus ? { entityStatus } : {}),
});

export const parseIssuePermissions = result => {
  if (!result?.ok) {
    return EMPTY_PERMISSIONS;
  }

  const data = result.data?.data || result.data || {};
  const { actions, ...fields } = data;

  return {
    actions: Array.isArray(actions) ? actions : [],
    fields: fields || {},
  };
};

export const fetchIssuePermissions = async (dataProvider, entityStatus) => {
  try {
    const result = await dataProvider.get(
      ISSUE_PERMISSIONS_API_ROOT,
      buildIssuePermissionsParams(entityStatus),
    );
    return parseIssuePermissions(result);
  } catch {
    return EMPTY_PERMISSIONS;
  }
};

// Fetches the issue entity permissions config on mount (and whenever the
// optional entityStatus changes). Returns { loading, actions, fields } where
// actions is a string[] of allowed entity-level actions and fields is the map
// of field name -> permission string.
export default function useIssuePermissions(entityStatus) {
  const dataProvider = useDataProvider(getProviderName());
  const [state, setState] = useState({ loading: true, ...EMPTY_PERMISSIONS });

  useEffect(() => {
    let active = true;
    // Fail closed while (re)fetching: stale grants must not stay actionable
    // if the provider or entityStatus changes mid-session.
    setState({ loading: true, ...EMPTY_PERMISSIONS });

    fetchIssuePermissions(dataProvider, entityStatus).then(permissions => {
      if (!active) {
        return;
      }
      setState({ loading: false, ...permissions });
    });

    return () => {
      active = false;
    };
  }, [dataProvider, entityStatus]);

  return state;
}
